// @ts-nocheck
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  Plus, 
  X, 
  Play, 
  Loader2,
  Trash2,
  Youtube
} from 'lucide-react';

const profileSchema = z.object({
  business_name: z.string().min(1, 'Business name is required'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  phone: z.string().optional()
});

type ProfileFormData = z.infer<typeof profileSchema>;

interface VideoVendorProfile {
  id: string;
  user_id: string;
  business_name?: string;
  description?: string;
  phone?: string;
  portfolio_videos?: string[];
  rating_average?: number;
  review_count?: number;
  is_active?: boolean;
}

interface ProfileManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorProfile: VideoVendorProfile | null;
  onProfileUpdate: () => void;
}

// Helper function to extract YouTube video ID from URL
const getYouTubeVideoId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// Helper function to get YouTube thumbnail
const getYouTubeThumbnail = (url: string): string => {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '/placeholder.svg';
};

const ProfileManagementModal: React.FC<ProfileManagementModalProps> = ({
  open,
  onOpenChange,
  vendorProfile,
  onProfileUpdate
}) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [portfolioVideos, setPortfolioVideos] = useState<string[]>(
    vendorProfile?.portfolio_videos || []
  );
  const [newVideoUrl, setNewVideoUrl] = useState('');

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      business_name: vendorProfile?.business_name || '',
      description: vendorProfile?.description || '',
      phone: vendorProfile?.phone || '',
    },
  });

  const handleAddYouTubeVideo = () => {
    if (!newVideoUrl.trim()) {
      toast.error('Please enter a YouTube URL');
      return;
    }

    const videoId = getYouTubeVideoId(newVideoUrl.trim());
    if (!videoId) {
      toast.error('Please enter a valid YouTube URL');
      return;
    }

    if (portfolioVideos.includes(newVideoUrl.trim())) {
      toast.error('This video has already been added');
      return;
    }

    setPortfolioVideos(prev => [...prev, newVideoUrl.trim()]);
    setNewVideoUrl('');
    toast.success('YouTube video added successfully!');
  };

  const handleDeleteVideo = (videoUrl: string) => {
    setPortfolioVideos(prev => prev.filter(url => url !== videoUrl));
    toast.success('Video removed successfully');
  };

  const onSubmit = async (data: ProfileFormData) => {
    if (!user?.id || !vendorProfile) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('video_vendor_profiles')
        .update({
          business_name: data.business_name,
          description: data.description,
          phone: data.phone,
          portfolio_videos: portfolioVideos,
        })
        .eq('id', vendorProfile.id);

      if (error) throw error;

      toast.success('Profile updated successfully!');
      onProfileUpdate();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Your Profile</DialogTitle>
          <DialogDescription>
            Update your business information and portfolio videos
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Business Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Business Information</h3>
              
              <FormField
                control={form.control}
                name="business_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Name *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Your video business name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        {...field} 
                        placeholder="Describe your video services and experience..."
                        className="min-h-[100px]"
                        maxLength={500}
                      />
                    </FormControl>
                    <div className="text-xs text-muted-foreground text-right">
                      {field.value?.length || 0}/500 characters
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="(555) 123-4567" type="tel" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Portfolio Videos */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Portfolio Videos</h3>
                <Youtube className="h-5 w-5 text-red-500" />
              </div>

              {/* Add YouTube Video */}
              <div className="flex gap-2">
                <Input
                  value={newVideoUrl}
                  onChange={(e) => setNewVideoUrl(e.target.value)}
                  placeholder="Paste YouTube video URL here..."
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddYouTubeVideo}
                  disabled={!newVideoUrl.trim()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Video
                </Button>
              </div>

              {/* Portfolio Video Grid */}
              {portfolioVideos.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {portfolioVideos.map((videoUrl, index) => (
                    <Card key={index} className="relative group">
                      <CardContent className="p-3">
                        <div className="aspect-video bg-muted rounded-lg relative flex items-center justify-center overflow-hidden">
                          <img
                            src={getYouTubeThumbnail(videoUrl)}
                            alt={`Video ${index + 1}`}
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                            <a
                              href={videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center"
                            >
                              <Play className="h-8 w-8 text-white" />
                            </a>
                          </div>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleDeleteVideo(videoUrl)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                          <div className="absolute bottom-2 left-2 bg-red-600 text-white px-1 py-0.5 text-xs rounded">
                            <Youtube className="h-3 w-3 inline" />
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 truncate">
                          YouTube Video {index + 1}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {portfolioVideos.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Youtube className="h-12 w-12 mx-auto mb-3 text-red-500" />
                  <p>No YouTube videos added yet</p>
                  <p className="text-sm">Add YouTube links to showcase your video work</p>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileManagementModal;